import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EchoGuest } from '@vtak/shared';
import { UserCheck, X, Eye, EyeOff } from 'lucide-react';
import { fetchJson, authHeaders } from '@/services/api-client';
import { CLIENT_CONFIG } from '@/config';

const unitGuestsKey = (unitId: string) => ['unit', 'guests', unitId] as const;

/** POST a per-guest action to the unit-guest admin API. */
async function guestAction(unitId: string, guestSub: string, action: string): Promise<void> {
  const res = await fetch(
    `${CLIENT_CONFIG.apiBase}/api/units/${encodeURIComponent(unitId)}/guests/${encodeURIComponent(guestSub)}/${action}`,
    authHeaders({ method: 'POST' }),
  );
  if (!res.ok) throw new Error(`guest ${action} failed: ${res.status}`);
}

/** A small labelled action button. */
function GuestButton({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded bg-white/[0.08] text-muted-foreground hover:text-foreground disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** One guest row with approve / view / disconnect controls. */
function GuestRow({ guest, unitId }: { guest: EchoGuest; unitId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const act = useMutation({
    mutationFn: (action: string) => guestAction(unitId, guest.userId, action),
    onSettled: () => queryClient.invalidateQueries({ queryKey: unitGuestsKey(unitId) }),
  });

  const pending = guest.status === 'pending';
  return (
    <li className="flex items-center gap-2 rounded-md bg-white/[0.04] px-2.5 py-1.5">
      <span className="flex-1 truncate text-xs text-foreground">{guest.email || guest.userId}</span>
      <span className={`text-[10px] uppercase ${pending ? 'text-amber-400' : 'text-green-400'}`}>{guest.status}</span>
      {pending ? (
        <GuestButton title="Approve as view-only guest" onClick={() => act.mutate('approve')} disabled={act.isPending}>
          <UserCheck className="h-3.5 w-3.5" />
        </GuestButton>
      ) : (
        <GuestButton
          title={guest.viewGranted ? 'Hide the mission area from this guest' : 'Show the mission area to this guest'}
          onClick={() => act.mutate(guest.viewGranted ? 'revoke-view' : 'grant-view')}
          disabled={act.isPending}
        >
          {guest.viewGranted ? <Eye className="h-3.5 w-3.5 text-echo-yellow" /> : <EyeOff className="h-3.5 w-3.5" />}
        </GuestButton>
      )}
      <GuestButton title="Disconnect guest" onClick={() => act.mutate('disconnect')} disabled={act.isPending}>
        <X className="h-3.5 w-3.5" />
      </GuestButton>
    </li>
  );
}

/**
 * Unit guests admin (admin only): QR scanners who chose "continue as guest" for
 * this unit, awaiting approval to a view-only mission-area map. Admins approve,
 * toggle the view, or disconnect them.
 */
export function UnitGuests({ unitId }: { unitId: string }): React.JSX.Element {
  const guests = useQuery({
    queryKey: unitGuestsKey(unitId),
    refetchInterval: 15_000,
    queryFn: () => fetchJson<EchoGuest[]>(`/api/units/${encodeURIComponent(unitId)}/guests`, []),
  });

  const list = guests.data ?? [];
  if (list.length === 0) {
    return (
      <p className="p-3 text-center text-xs text-muted-foreground">
        No guests yet. Share the unit’s join QR — scanners can enter as view-only guests.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1 p-3">
      {list.map((guest) => (
        <GuestRow key={guest.userId} guest={guest} unitId={unitId} />
      ))}
    </ul>
  );
}
